import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";
import { EduSubmission } from "../types";

export default function TeacherReview() {
  const navigate = useNavigate();
  const { huntId } = useParams();
  const [submissions, setSubmissions] = useState<EduSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      if (!u || u.isAnonymous) navigate("/teacher");
    });
  }, [navigate]);

  useEffect(() => {
    if (!huntId) return;
    const q = query(
      collection(db, "edu_submissions"),
      where("huntId", "==", huntId),
      orderBy("submittedAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setError(null);
        setSubmissions(
          snap.docs.map((d) => ({
            ...(d.data() as EduSubmission),
            submissionId: d.id,
          }))
        );
        setLoading(false);
      },
      // A listener with no error callback fails silently: a denied read or a
      // missing composite index left this screen on "Loading..." forever.
      (e) => {
        setError(e.message);
        setLoading(false);
      }
    );
    return unsub;
  }, [huntId]);

  // A takedown that fails silently is worse than one that errors: a teacher
  // who taps Remove and navigates away believes an inappropriate child photo
  // is gone. Surface failures through the same error line the listener uses,
  // and block double-taps while the write is in flight.
  const [removing, setRemoving] = useState<string | null>(null);
  const remove = async (s: EduSubmission) => {
    setRemoving(s.submissionId);
    setError(null);
    try {
      await updateDoc(doc(db, "edu_submissions", s.submissionId), {
        moderationStatus: "removed",
      });
    } catch (e) {
      setError(
        `Could not remove that photo (${e instanceof Error ? e.message : "unknown error"}). It is still visible; try again.`
      );
    } finally {
      setRemoving(null);
    }
  };

  if (loading) {
    return (
      <main className="screen">
        <div className="card wide"><p className="muted">Loading...</p></div>
      </main>
    );
  }

  return (
    <main className="screen">
      <div className="card wide">
        <Link to="/teacher/dashboard" className="link">← Dashboard</Link>
        <h1>Submissions</h1>
        {error && <p className="error">{error}</p>}
        {submissions.length === 0 ? (
          <p className="muted">
            No submissions yet. Share the class code with your students.
          </p>
        ) : (
          <ul className="grid">
            {submissions.map((s) => (
              <li key={s.submissionId} className={`thumb ${s.moderationStatus}`}>
                <img src={s.photoUrl} alt={s.challengeText} />
                <div className="meta">
                  <strong>{s.studentName}</strong>
                  <small>{s.challengeText}</small>
                  <small className={`badge ${s.moderationStatus}`}>
                    {s.moderationStatus}
                  </small>
                  {s.moderationStatus !== "removed" && (
                    <button
                      className="link"
                      onClick={() => remove(s)}
                      disabled={removing === s.submissionId}
                    >
                      {removing === s.submissionId ? "Removing..." : "Remove"}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
